import { create } from "zustand"
import { persist } from "zustand/middleware"

const DEBUG_SOURCE_STORE = process.env.NEXT_PUBLIC_DEBUG_SOURCE_STORE === "1"
const sourceLog = (...args: unknown[]) => {
  if (DEBUG_SOURCE_STORE) console.log(...args)
}

export type SourceType = "booru" | "api" | "rss" | "custom"
/**
 * What KIND of source this is, which decides how it is set up (paging, album strip, detail fetch). Approved by the owner 2026-10-23 as part
 * of the source audit; set per source as each is audited. booru = post feeds (Rule34, Gelbooru, Danbooru), gallery = doujin and manga
 * (Hitomi, Doujins, HentaiEra, LewdVault, GoonTai), album = Erome, video = XVideos / VR sites / RedGifs, audio = AudioChan, model = Civitai,
 * creator = Kemono / Pawchive. Not set yet on sources not audited.
 */
export type SourceKind = "booru" | "gallery" | "album" | "video" | "audio" | "model" | "creator"
export type AuthType = "none" | "api_key" | "optional_api_key" | "oauth" | "basic" | "cookie"

export interface SourceAuth {
  type: AuthType
  apiKey?: string
  apiKeyParam?: string // e.g., "api_key" or "key"
  userId?: string
  userIdParam?: string // e.g., "user_id"
  username?: string
  password?: string
  cookieValue?: string
  oauthToken?: string
}

export interface SourceEndpoints {
  search: string // Main search endpoint
  tags?: string // Tag autocomplete endpoint
  post?: string // Single post endpoint
  gallery?: string // Gallery/album detail endpoint (doujins, hentaiera)
}

export interface SourceConfig {
  id: string
  name: string
  type: SourceType
  enabled: boolean
  disabled?: boolean // Mark source as completely out of service
  /** An out-of-service source that Manage Sources still lists, greyed out at the very bottom (others are hidden there). */
  showWhenDisabled?: boolean
  /** See SourceKind. Unset on sources that have not been audited yet. */
  kind?: SourceKind
  useBooruPackage?: boolean // Use the booru npm package for this source
  fetchMode?: "server" | "client" // Server-side (API route) or client-side (browser direct)
  // Drives "Auto" loading mode (see booru-browser.tsx's resolveLoadingMode).
  // Most sources stream a real cursor/offset-based infinite feed; a few
  // (xlecx) only expose numbered pages with no infinite concept at all, so
  // those need Auto to resolve to pagination instead. Undefined = infinite-scroll.
  preferredLoadingMode?: "infinite-scroll" | "pagination"
  apiType?:
    | "gelbooru"
    | "gelbooruv1"
    | "gelboorualike"
    | "danbooru"
    | "booruonrails"
    | "moebooru"
    | "paheal"
    | "e621"
    | "e926"
    | "e6ai"
    | "generic"
    | "erome"
    | "xvideos"
    | "eporner-vr"
    | "vrpornbase"
    | "arfreaks"
    | "sexvr"
    | "vrhump"
    | "safebooru"
    | "rule34video"
    | "agnph"
    | "hydrus"
    | "inkbunny"
    | "nyanpals"
    | "philomena"
    | "rainbooru"
    | "realbooru"
    | "r34hentai"
    | "r34us"
    | "rule34hentai"
    | "rule34xyz"
    | "rule34us"
    | "rule34lol"
    | "sankaku"
    | "idolsankaku"
    | "shimmie"
    | "szurubooru"
    | "wildcritters"
    | "world"
    | "4chan"
    | "tbib"
    | "hypnohub"
    | "derpibooru"
    | "footfetishbooru"
    | "hitomi"
    | "doujins"
    | "hentaiera"
    | "lewdvault"
    | "xlecx"
    | "civitai"
    | "redgifs"
    | "scrolller"
    | "kemono"
    | "pawchive"
    | "goontai"
    | "javapi"
    | "javapi_hentai"
  endpoints: SourceEndpoints
  auth: SourceAuth
  boards?: {
    available: { id: string; name: string; description?: string; risky?: boolean }[]
    selected: string[]
    allowMultiple?: boolean
  }
  tagBlacklist?: string[] // Tags to filter out (e.g., ["gore", "death", "rekt"])
  thumbnailQuality?: "low" | "full" // For 4chan: low = 125x125 thumbnail, full = original image
  // Parsing configuration
  responseFormat: "json" | "xml" | "html"
  dataPath?: string // JSON path to posts array, e.g., "post" or "data.posts"
  // Field mappings for parsing responses
  fieldMappings: {
    id: string
    fileUrl: string
    previewUrl: string
    sampleUrl?: string
    tags: string
    tagsDelimiter?: string // e.g., " " or ","
    rating?: string
    score?: string
    source?: string
    width?: string
    height?: string
  }
  // Pagination
  pagination: {
    type: "page" | "offset" | "cursor" | "id"
    param: string // e.g., "pid", "page", "offset", "max_id"
    startAt: number // 0 or 1
  }
  // Limits
  limitParam: string // e.g., "limit"
  maxLimit: number
  tagLimit?: number // Max tags per search (like Danbooru's 2)
  // Rating mappings
  ratingMappings?: {
    safe: string[]
    questionable: string[]
    explicit: string[]
  }
  // Metadata
  icon?: string
  faviconUrl?: string
  description?: string
  homepage?: string
  isDefault: boolean
  isCustom?: boolean
  requiresAuth?: boolean // If false, don't show auth prompts for this source
  // NOTE: for sources that have no credential system AT ALL, don't rely on
  // this field alone — add the id to SOURCES_WITHOUT_CREDENTIALS below. See
  // that constant for why.
  // Ad tier gating — "safe" for sources that are SFW-only by design (not just
  // "mostly safe" or rating-filterable). Undefined = source can carry explicit
  // content, so ads default to the max tier. See lib/ad-tier.ts.
  contentRating?: "safe"
  defaultNestedMode?: boolean // Default navigation mode for albums (true = nested grid, false = modal, undefined = use global setting)
  heliotropeInstances?: string[] // For Hitomi.la only - list of Heliotrope mirror instances
  createdAt: string
  updatedAt: string
}

interface SourceStore {
  sources: SourceConfig[]

  // Actions
  addSource: (source: Omit<SourceConfig, "id" | "createdAt" | "updatedAt">) => void
  updateSource: (id: string, updates: Partial<SourceConfig>) => void
  deleteSource: (id: string) => void
  toggleSource: (id: string) => void
  duplicateSource: (id: string) => void
  testConnection: (id: string) => Promise<{ success: boolean; error?: string }>
  reorderSources: (startIndex: number, endIndex: number) => void // Added reorder method
  getAllSources: () => SourceConfig[] // Added getAllSources method
  getEnabledSources: () => SourceConfig[]
  resetToDefaults: () => void
  exportSources: () => string
  importSources: (json: string) => boolean
  getSource: (id: string) => SourceConfig | undefined
}

/**
 * Sources that have NO credential system whatsoever — no API keys, no
 * accounts we can authenticate against, nothing a user could type into a
 * credentials dialog that would change anything.
 *
 * This deliberately duplicates `requiresAuth: false` on the configs below,
 * because that field alone has proven not to be enough: SourceConfig objects
 * are persisted (zustand `persist`) and merged back on load, so a config
 * saved before `requiresAuth: false` existed keeps whatever it was stored
 * with, and every guard that reads `source.requiresAuth` fails open and
 * prompts anyway. Migrations patch that up for known versions, but anything
 * that reintroduces a stale config — an older persisted blob, an import, a
 * synced/restored profile — brings the prompt back with it. Reported
 * repeatedly for Erome, which has no API at all.
 *
 * A hardcoded id list can't be invalidated by stored data, so use
 * `sourceHasNoCredentials()` at every point that would show or offer a
 * credentials prompt.
 */
export const SOURCES_WITHOUT_CREDENTIALS = new Set<string>(["erome"])

/** True when this source has no credential system, so no auth UI should ever be shown for it. */
export function sourceHasNoCredentials(source: string | SourceConfig | null | undefined): boolean {
  if (!source) return false
  const id = typeof source === "string" ? source : source.id
  if (SOURCES_WITHOUT_CREDENTIALS.has(id)) return true
  return typeof source !== "string" && source.requiresAuth === false
}

// Default built-in sources
const BASE_DEFAULT_SOURCES: SourceConfig[] = [
  {
    id: "safebooru",
    name: "Safebooru",
    type: "booru",
    enabled: true,  // Default ON — safe content for new users
    contentRating: "safe", // SFW-only by design — no explicit content is hosted
    useBooruPackage: false, // Disabled - use direct API calls instead
    apiType: "safebooru",
    endpoints: {
      search: "https://safebooru.org/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://safebooru.org/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "post",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=safebooru.org&sz=64",
    description: "Safebooru is a safe-for-work imageboard.",
    homepage: "https://safebooru.org",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "danbooru",
    name: "Danbooru",
    type: "booru",
    enabled: false, // Disabled by default - user can enable if needed
    fetchMode: "client", // Use client-side to bypass Cloudflare
    useBooruPackage: false, // Disabled - booru package uses server-side which gets blocked by Cloudflare
    apiType: "danbooru",
    endpoints: {
      search: "https://danbooru.donmai.us/posts.json",
      tags: "https://danbooru.donmai.us/tags.json",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "api_key",
      apiKey: "", // User credentials only - use auth dialog
      userIdParam: "login",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_file_url",
      sampleUrl: "large_file_url",
      tags: "tag_string",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "image_width",
      height: "image_height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=danbooru.donmai.us&sz=64",
    description: "Danbooru is a community imageboard that focuses on copyright-free images.",
    homepage: "https://danbooru.donmai.us",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "yandere",
    name: "Yande.re",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "danbooru",
    endpoints: {
      search: "https://yande.re/post.json",
      tags: "https://yande.re/tag.json",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "password_hash",
      userIdParam: "login",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 100,
    tagLimit: 6,
    ratingMappings: {
      safe: ["s", "safe"],
      questionable: ["q", "questionable"],
      explicit: ["e", "explicit"],
    },
    icon: "🎨",
    faviconUrl: "https://www.google.com/s2/favicons?domain=yande.re&sz=64",
    description: "Yande.re is a high-quality anime image board with strict quality standards. Works without credentials.",
    homepage: "https://yande.re",
    isDefault: true,
    isCustom: false,
    requiresAuth: false, // Disable auth prompts - site works fine without credentials
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "sakugabooru",
    name: "Sakugabooru",
    type: "booru",
    enabled: false,
    // Out of service since 2026-10-11: Cloudflare refuses every server address we can reach it from (Vercel directly, and the
    // relay Worker from two Cloudflare cities; a login does not change that). Listed greyed out at the bottom of Manage Sources.
    // Remove these two lines to bring it back (the fetch code, relay retry and SAKUGABOORU_* login are all still in place).
    disabled: true,
    showWhenDisabled: true,
    contentRating: "safe", // Animation/sakuga clips — SFW-focused by design
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "moebooru",
    endpoints: {
      search: "https://www.sakugabooru.com/post.json",
      tags: "https://www.sakugabooru.com/tag.json",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "password_hash",
      userIdParam: "login",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 100,
    ratingMappings: {
      safe: ["s", "safe"],
      questionable: ["q", "questionable"],
      explicit: ["e", "explicit"],
    },
    icon: "🎬",
    faviconUrl: "https://www.google.com/s2/favicons?domain=sakugabooru.com&sz=64",
    description: "Unavailable right now: Sakugabooru blocks our servers, so it cannot be used here. We will switch it back on if that changes.",
    homepage: "https://www.sakugabooru.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "erome",
    name: "Erome",
    type: "booru",
    enabled: false,
    apiType: "erome",
    fetchMode: "server",
    endpoints: {
      search: "https://www.erome.com/explore",
    },
    auth: {
      type: "none",
    },
    requiresAuth: false, // Erome has no public API key/credential system — don't prompt for one
    boards: {
      available: [
        { id: "all", name: "All" },
        { id: "straight", name: "Straight" },
        { id: "gay", name: "Gay" },
        { id: "hentai", name: "Hentai" },
        { id: "trans", name: "Trans" },
      ],
      selected: ["straight"],
      allowMultiple: false,
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.erome.com&sz=64",
    description: "Erome is an adult content sharing platform.",
    homepage: "https://www.erome.com",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "xvideos",
    name: "XVideos",
    type: "booru",
    enabled: false,
    apiType: "xvideos",
    fetchMode: "server",
    endpoints: {
      search: "https://www.xvideos.com",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "p",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 54, // XVideos shows ~54 videos per page
    icon: "🎬",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.xvideos.com&sz=64",
    description: "XVideos - Adult video platform with a large video library. No account required.",
    homepage: "https://www.xvideos.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "eporner-vr",
    name: "EpornerVR",
    type: "booru",
    enabled: false,
    apiType: "eporner-vr",
    fetchMode: "server",
    endpoints: {
      search: "https://www.eporner.com/api/v2/video/search/",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "videos",
    fieldMappings: {
      id: "id",
      fileUrl: "url",
      previewUrl: "default_thumb.src",
      sampleUrl: "default_thumb.src",
      tags: "keywords",
      tagsDelimiter: ",",
      rating: "rating",
      score: "rate",
      source: "url",
      width: "default_thumb.width",
      height: "default_thumb.height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "per_page",
    maxLimit: 100,
    icon: "🥽",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.eporner.com&sz=64",
    description:
      "EpornerVR - VR content from Eporner. Tapping a video opens it in the VR page's browser tab rather than playing inline.",
    homepage: "https://www.eporner.com/tag/ar-vr/",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "vrpornbase",
    name: "VR Porn Base",
    type: "booru",
    enabled: false,
    apiType: "vrpornbase",
    fetchMode: "server",
    endpoints: {
      search: "https://vrpornbase.com",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 24,
    icon: "🥽",
    faviconUrl: "https://www.google.com/s2/favicons?domain=vrpornbase.com&sz=64",
    description: "VR Porn Base - BadoinkVR-produced VR videos. No account required.",
    homepage: "https://vrpornbase.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "arfreaks",
    name: "AR Freaks",
    type: "booru",
    enabled: false,
    apiType: "arfreaks",
    fetchMode: "server",
    endpoints: {
      search: "https://www.arfreaks.com",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 24,
    icon: "🥽",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.arfreaks.com&sz=64",
    description: "AR Freaks - Free AR/passthrough VR videos, full length. No account required.",
    homepage: "https://www.arfreaks.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "sexvr",
    name: "SexVR",
    type: "booru",
    enabled: false,
    apiType: "sexvr",
    fetchMode: "server",
    endpoints: {
      search: "https://www.sexvr.com",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 24,
    icon: "🥽",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.sexvr.com&sz=64",
    description: "SexVR - Free VR videos, full length, multiple quality options. No account required.",
    homepage: "https://www.sexvr.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "vrhump",
    name: "VR Hump",
    type: "booru",
    enabled: false,
    apiType: "vrhump",
    fetchMode: "server",
    endpoints: {
      search: "https://www.vrhump.com",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 24,
    icon: "🥽",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.vrhump.com&sz=64",
    description: "VRHump - Free VR videos, full length. No account required.",
    homepage: "https://www.vrhump.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "rule34",
    name: "Rule34.xxx",
    type: "booru",
    enabled: false,
    useBooruPackage: false, // Disabled - use direct API calls instead
    apiType: "gelbooru",
    endpoints: {
      search: "https://api.rule34.xxx/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://api.rule34.xxx/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "api_key", // REQUIRED - Rule34.xxx API now requires authentication
      apiKeyParam: "api_key",
      apiKey: "", // Get your API key from api.rule34.xxx
      userId: "", // Your user ID from Rule34.xxx
      userIdParam: "user_id",
    },
    responseFormat: "json",
    dataPath: "post",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=rule34.xxx&sz=64",
    description: "Rule34.xxx - Imageboard with a wide variety of content. Requires a free account key from rule34.xxx.",
    homepage: "https://rule34.xxx",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "gelbooru",
    name: "Gelbooru",
    type: "booru",
    enabled: true,
    useBooruPackage: false, // Disabled - use direct API calls instead
    fetchMode: "server", // Use server-side fetching to avoid CORS issues
    apiType: "gelbooru",
    endpoints: {
      search: "https://gelbooru.com/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://gelbooru.com/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "api_key", // Changed from optional_api_key - Gelbooru now requires API key
      apiKey: "",
      apiKeyParam: "api_key",
      userId: "",
      userIdParam: "user_id",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=gelbooru.com&sz=64",
    description: "Gelbooru - Large imageboard with millions of anime-style images. Requires a free account key from gelbooru.com.",
    homepage: "https://gelbooru.com",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "xbooru",
    name: "Xbooru",
    type: "booru",
    enabled: false,
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "gelbooru",
    endpoints: {
      search: "https://xbooru.com/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://xbooru.com/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "optional_api_key",
      apiKey: "",
      apiKeyParam: "api_key",
      userId: "",
      userIdParam: "user_id",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🔞",
    faviconUrl: "https://www.google.com/s2/favicons?domain=xbooru.com&sz=64",
    description: "Adult imageboard with anime and hentai content.",
    homepage: "https://xbooru.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "tbib",
    name: "TBIB",
    type: "booru",
    enabled: true,
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "tbib",
    endpoints: {
      search: "https://tbib.org/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://tbib.org/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "hash",
      previewUrl: "hash",
      sampleUrl: "hash",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100, // 100 posts per request means about half the requests while scrolling; TBIB answers 100 fine (checked 2026-10-26)
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=tbib.org&sz=64",
    description: "The Big ImageBoard (TBIB) - General imageboard with a large tagged collection. Up to 100 posts per search.",
    homepage: "https://tbib.org",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "zerochan",
    name: "Zerochan",
    type: "booru",
    enabled: false,
    contentRating: "safe", // SFW anime wallpaper site — explicit content is banned
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "generic",
    endpoints: {
      search: "https://www.zerochan.net",
      tags: "",
    },
    auth: {
      type: "basic",
      username: "",
      password: "",
    },
    responseFormat: "json",
    dataPath: "items",
    fieldMappings: {
      id: "id",
      fileUrl: "large",
      previewUrl: "thumbnail",
      sampleUrl: "full",
      tags: "tags",
      tagsDelimiter: ",",
      rating: "safe",
      score: "",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "p",
      startAt: 1,
    },
    limitParam: "l",
    maxLimit: 250,
    icon: "🎨",
    faviconUrl: "https://www.google.com/s2/favicons?domain=zerochan.net&sz=64",
    description: "High-quality anime imageboard. Add your Zerochan username in Source Settings for best results.",
    homepage: "https://www.zerochan.net",
    isDefault: true,
    isCustom: false,
    requiresAuth: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "sankaku",
    name: "Sankaku Complex",
    type: "booru",
    enabled: false,
    apiType: "sankaku",
    endpoints: {
      search: "https://capi-v2.sankakucomplex.com/posts",
      tags: "https://capi-v2.sankakucomplex.com/tags",
    },
    auth: {
      type: "basic", // Username + Password that exchanges for bearer token
      username: "",
      password: "",
    },
    responseFormat: "json",
    dataPath: "", // Response is a direct array, not nested
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "fav_count",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 40,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=sankakucomplex.com&sz=64",
    description: "Sankaku Complex - Login with your username and password to access content.",
    homepage: "https://www.sankakucomplex.com",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "idolsankaku",
    name: "Idol Complex",
    type: "booru",
    enabled: false,
    apiType: "idolsankaku",
    endpoints: {
      search: "https://iapi.sankakucomplex.com/posts",
      tags: "https://iapi.sankakucomplex.com/tags",
    },
    auth: {
      type: "basic", // Username + Password that exchanges for bearer token
      username: "",
      password: "",
    },
    responseFormat: "json",
    dataPath: "", // Response is a direct array, not nested
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "fav_count",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 40,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=idolcomplex.com&sz=64",
    description: "Idol Complex - Login with your username and password to access content.",
    homepage: "https://idolcomplex.com",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "paheal",
    name: "Paheal",
    type: "booru",
    enabled: false,
    useBooruPackage: false, // Disabled - use direct API calls instead
    apiType: "paheal",
    endpoints: {
      search: "https://rule34.paheal.net/post/list",
      tags: "https://rule34.paheal.net/api/internal/autocomplete",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 20,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=rule34.paheal.net&sz=64",
    description: "Paheal imageboard.",
    homepage: "https://rule34.paheal.net",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "rule34xyz",
    name: "Rule34.xyz",
    type: "booru",
    enabled: true,
    apiType: "rule34xyz",
    fetchMode: "server",
    endpoints: {
      search: "https://rule34.xyz/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://rule34.xyz/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "post",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 50,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=rule34.xyz&sz=64",
    description: "Rule34.xyz - General imageboard. No account required.",
    homepage: "https://rule34.xyz",
    isDefault: true,
    isCustom: false,
    requiresAuth: false, // Disable auth prompts for this source
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "rule34us",
    name: "Rule34.us",
    type: "booru",
    enabled: false,
    apiType: "rule34us",
    fetchMode: "server",
    endpoints: {
      search: "https://rule34.us/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://rule34.us/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "post",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=rule34.us&sz=64",
    description: "Rule34.us - General imageboard.",
    homepage: "https://rule34.us",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "rule34lol",
    name: "Rule34.lol",
    type: "booru",
    enabled: false,
    apiType: "rule34lol",
    fetchMode: "server",
    endpoints: {
      search: "https://rule34.lol/",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 20,
    icon: "🖼️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=rule34.lol&sz=64",
    description: "Rule34.lol - General imageboard. No account required.",
    homepage: "https://rule34.lol",
    isDefault: true,
    isCustom: false,
    requiresAuth: false, // Disable auth prompts - site works without credentials
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "4chan",
    name: "4chan",
    type: "booru",
    enabled: false,
    apiType: "4chan",
    fetchMode: "server",
    endpoints: {
      search: "https://a.4cdn.org",
    },
    auth: {
      type: "none",
    },
    thumbnailQuality: "low", // Default to low quality (125x125) for faster loading
    boards: {
      available: [
        { id: "gif", name: "/gif/ - Adult GIF", description: "Animated adult content" },
        { id: "s", name: "/s/ - Sexy Beautiful Women", description: "SFW-ish women" },
        { id: "hc", name: "/hc/ - Hardcore", description: "Hardcore pornography" },
        { id: "hm", name: "/hm/ - Handsome Men", description: "Attractive men" },
        { id: "h", name: "/h/ - Hentai", description: "Hentai artwork" },
        { id: "e", name: "/e/ - Ecchi", description: "Ecchi/softcore hentai" },
        { id: "u", name: "/u/ - Yuri", description: "Yuri/lesbian hentai" },
        { id: "d", name: "/d/ - Hentai/Alternative", description: "Alternative hentai" },
        { id: "y", name: "/y/ - Yaoi", description: "Yaoi/gay hentai" },
        { id: "hr", name: "/hr/ - High Resolution", description: "High-res images" },
        { id: "aco", name: "/aco/ - Adult Cartoons", description: "Western adult cartoons" },
        { id: "r", name: "/r/ - Adult Requests", description: "Request adult content" },
        { id: "wg", name: "/wg/ - Wallpapers/General", description: "Wallpapers (some NSFW)" },
        { id: "cm", name: "/cm/ - Cute/Male", description: "Cute anime boys" },
        { id: "c", name: "/c/ - Anime/Cute", description: "Cute anime girls (SFW)" },
        { id: "w", name: "/w/ - Anime/Wallpapers", description: "Anime wallpapers" },
        { id: "wsr", name: "/wsr/ - Worksafe Requests", description: "SFW requests" },
        { id: "b", name: "/b/ - Random", description: "Random content", risky: true },
      ],
      selected: [], // none until a board is picked in the 4chan strip, so a search never loads a dozen boards at once
      allowMultiple: false,
    },
    tagBlacklist: ["gore", "death", "rekt", "dead", "corpse", "murder", "suicide", "blood", "injury", "accident", "fatal"],
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "no",
      fileUrl: "tim",
      previewUrl: "tim",
      sampleUrl: "tim",
      tags: "com",
      tagsDelimiter: " ",
      rating: "rating",
      score: "replies",
      source: "source",
      width: "w",
      height: "h",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 150,
    icon: "🍀",
    faviconUrl: "https://www.google.com/s2/favicons?domain=4chan.org&sz=64",
    description: "4chan - Imageboard with multiple NSFW boards. Gore/death content automatically filtered. Select boards in source settings.",
    homepage: "https://4chan.org",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    defaultNestedMode: true, // 4chan threads should use nested grid by default
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "hypnohub",
    name: "Hypnohub",
    type: "booru",
    enabled: false,
    // Not the booru package any more (2026-10-27): that route (a network call from our server back into itself) paged one page ahead, so the
    // newest 100 posts were never shown, and it never saw the best-of strip's window. Our own Gelbooru 0.2 reader pages by pid like Safebooru.
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "hypnohub",
    endpoints: {
      search: "https://hypnohub.net/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://hypnohub.net/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "🌀",
    faviconUrl: "https://www.google.com/s2/favicons?domain=hypnohub.net&sz=64",
    description: "Hypnohub - Hypnosis-themed imageboard featuring hypnosis and mind control content.",
    homepage: "https://hypnohub.net",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "e621",
    name: "e621",
    type: "booru",
    enabled: true,
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "e621",
    endpoints: {
      search: "https://e621.net/posts.json",
      tags: "https://e621.net/tags.json",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "api_key",
      userIdParam: "login",
    },
    responseFormat: "json",
    dataPath: "posts",
    fieldMappings: {
      id: "id",
      fileUrl: "file.url",
      previewUrl: "preview.url",
      sampleUrl: "sample.url",
      tags: "tags.general",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score.total",
      source: "sources",
      width: "file.width",
      height: "file.height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 320,
    tagLimit: 40,
    ratingMappings: {
      safe: ["s", "safe"],
      questionable: ["q", "questionable"],
      explicit: ["e", "explicit"],
    },
    icon: "🐾",
    faviconUrl: "https://www.google.com/s2/favicons?domain=e621.net&sz=64",
    description: "e621 - Furry artwork imageboard with comprehensive tagging. Supports images, videos, and animations. Optional account key for higher limits.",
    homepage: "https://e621.net",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "e926",
    name: "e926",
    type: "booru",
    enabled: false,
    contentRating: "safe", // SFW counterpart of e621 — explicit content is disallowed
    // Back in service 2026-10-13: the owner added the site's account (E926_USERNAME / API_KEY_E926), which the e621 search sends as
    // Basic auth, and an authenticated request gets through. Still switched off by default for visitors (enabled: false).
    useBooruPackage: false,
    fetchMode: "client", // e926 shuts out every datacenter address (Vercel, Cloudflare), so the visitor's browser asks it: lib/e621-client.ts
    apiType: "e926",
    endpoints: {
      search: "https://e926.net/posts.json",
      tags: "https://e926.net/tags.json",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "api_key",
      userIdParam: "login",
    },
    responseFormat: "json",
    dataPath: "posts",
    fieldMappings: {
      id: "id",
      fileUrl: "file.url",
      previewUrl: "preview.url",
      sampleUrl: "sample.url",
      tags: "tags.general",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score.total",
      source: "sources",
      width: "file.width",
      height: "file.height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 320,
    tagLimit: 40,
    ratingMappings: {
      safe: ["s", "safe"],
      questionable: ["q", "questionable"],
      explicit: ["e", "explicit"],
    },
    icon: "🐾",
    faviconUrl: "https://www.google.com/s2/favicons?domain=e926.net&sz=64",
    description: "e926 - the safe-for-work side of e621: furry art, no explicit content.",
    homepage: "https://e926.net",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "e6ai",
    name: "e6AI",
    type: "booru",
    enabled: false,
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "e6ai",
    endpoints: {
      search: "https://e6ai.net/posts.json",
      tags: "https://e6ai.net/tags.json",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "api_key",
      userIdParam: "login",
    },
    responseFormat: "json",
    dataPath: "posts",
    fieldMappings: {
      id: "id",
      fileUrl: "file.url",
      previewUrl: "preview.url",
      sampleUrl: "sample.url",
      tags: "tags.general",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score.total",
      source: "sources",
      width: "file.width",
      height: "file.height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 320,
    tagLimit: 40,
    ratingMappings: {
      safe: ["s", "safe"],
      questionable: ["q", "questionable"],
      explicit: ["e", "explicit"],
    },
    icon: "🤖",
    faviconUrl: "https://www.google.com/s2/favicons?domain=e6ai.net&sz=64",
    description: "e6AI - AI-generated furry artwork imageboard. Optional account key for higher limits.",
    homepage: "https://e6ai.net",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "derpibooru",
    name: "Derpibooru",
    type: "booru",
    enabled: false,
    // Not the booru package: that path cannot use the server's API key (API_KEY_DERPIBOORU). The Philomena search in
    // lib/booru-sources/booru-api/philomena.ts can, and handles the empty default feed.
    useBooruPackage: false,
    fetchMode: "server",
    apiType: "derpibooru",
    endpoints: {
      search: "https://derpibooru.org/api/v1/json/search/images",
      tags: "https://derpibooru.org/api/v1/json/search/tags",
    },
    auth: {
      type: "optional_api_key",
      apiKeyParam: "key",
    },
    responseFormat: "json",
    dataPath: "images",
    fieldMappings: {
      id: "id",
      fileUrl: "representations.full",
      previewUrl: "representations.thumb_tiny",
      sampleUrl: "representations.large",
      tags: "tags",
      tagsDelimiter: ",",
      rating: "content_rating",
      score: "score",
      source: "source_url",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "per_page",
    maxLimit: 50,
    icon: "🦄",
    faviconUrl: "https://www.google.com/s2/favicons?domain=derpibooru.org&sz=64",
    description: "Derpibooru - My Little Pony imageboard using Philomena software. Features pony artwork and fan content.",
    homepage: "https://derpibooru.org",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "realbooru",
    name: "Realbooru",
    type: "booru",
    enabled: true, // Re-enabled with HTML scraping
    disabled: false, // No longer out of service - using scraping
    useBooruPackage: false, // Disabled - using HTML scraping instead
    fetchMode: "server",
    apiType: "realbooru",
    endpoints: {
      search: "https://realbooru.com/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://realbooru.com/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 100,
    icon: "📸",
    faviconUrl: "https://www.google.com/s2/favicons?domain=realbooru.com&sz=64",
    description: "Realbooru - Real photo imageboard, tag-based search.",
    homepage: "https://realbooru.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "rule34hentai",
    name: "Rule34Hentai.net",
    type: "booru",
    enabled: false,
    apiType: "rule34hentai",
    fetchMode: "server",
    endpoints: {
      search: "https://rule34hentai.net/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://rule34hentai.net/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "post",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 42,
    icon: "🔞",
    faviconUrl: "https://www.google.com/s2/favicons?domain=rule34hentai.net&sz=64",
    description: "Rule34Hentai.net - Shimmie imageboard (paheal-style tags). Search by tag, multiple tags, -tag to exclude.",
    homepage: "https://rule34hentai.net",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "footfetishbooru",
    name: "Foot Fetish Booru",
    type: "booru",
    enabled: false,
    apiType: "footfetishbooru",
    fetchMode: "server",
    endpoints: {
      search: "https://footfetishbooru.booru.org/index.php?page=dapi&s=post&q=index&json=1",
      tags: "https://footfetishbooru.booru.org/index.php?page=dapi&s=tag&q=index&json=1",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "file_url",
      previewUrl: "preview_url",
      sampleUrl: "sample_url",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "pid",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 40,
    icon: "🦶",
    faviconUrl: "https://www.google.com/s2/favicons?domain=footfetishbooru.booru.org&sz=64",
    description: "Foot Fetish Booru: foot-focused art and pictures, read from the board's own pages. The board takes two search terms; a third is filtered here.",
    homepage: "https://footfetishbooru.booru.org",
    isDefault: true,
    isCustom: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "hitomi",
    name: "Hitomi.la",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    apiType: "hitomi",
    kind: "gallery",
    defaultNestedMode: true,
    heliotropeInstances: [
      "https://heliotrope.saebasol.org",
      "https://inst.psec.dev"
    ],
    endpoints: {
      search: "https://ltn.hitomi.la/index-all.nozomi",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "cover",
      previewUrl: "cover",
      sampleUrl: "cover",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "offset",
      param: "offset",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 25,
    icon: "📚",
    faviconUrl: "https://www.google.com/s2/favicons?domain=hitomi.la&sz=64",
    description: "Hitomi.la - Manga and doujinshi.",
    homepage: "https://hitomi.la",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "doujins",
    name: "Doujins.com",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    apiType: "doujins",
    kind: "gallery",
    defaultNestedMode: true,
    endpoints: {
      search: "/api/booru/doujins/search",
      gallery: "/api/booru/doujins/gallery"
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "coverUrl",
      previewUrl: "coverUrl",
      sampleUrl: "coverUrl",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 25,
    icon: "📚",
    faviconUrl: "https://www.google.com/s2/favicons?domain=doujins.com&sz=64",
    description: "Doujins.com - English-translated hentai doujinshi with audio support.",
    homepage: "https://doujins.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "hentaiera",
    name: "HentaiEra",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    apiType: "hentaiera",
    kind: "gallery",
    defaultNestedMode: true,
    endpoints: {
      search: "/api/booru/hentaiera/search",
      gallery: "/api/booru/hentaiera/gallery"
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "coverUrl",
      previewUrl: "coverUrl",
      sampleUrl: "coverUrl",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 25,
    icon: "📕",
    faviconUrl: "https://www.google.com/s2/favicons?domain=hentaiera.com&sz=64",
    description: "HentaiEra - Hentai manga, doujinshi and comic galleries with category tabs.",
    homepage: "https://hentaiera.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "lewdvault",
    name: "LewdVault",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    apiType: "lewdvault",
    defaultNestedMode: true,
    endpoints: {
      search: "/api/booru/lewdvault/search",
      gallery: "/api/booru/lewdvault/gallery"
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "coverUrl",
      previewUrl: "coverUrl",
      sampleUrl: "coverUrl",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 25,
    icon: "🔒",
    faviconUrl: "https://www.google.com/s2/favicons?domain=lewdvault.com&sz=64",
    description: "LewdVault - Album-based fanart archive, always freshly loaded.",
    homepage: "https://lewdvault.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "xlecx",
    name: "XlecX",
    type: "booru",
    enabled: true,
    apiType: "xlecx",
    preferredLoadingMode: "pagination", // XlecX only has numbered pages, no real infinite feed
    endpoints: {
      search: "https://xlecx.one/",
      tags: "https://xlecx.one/tags/",
    },
    auth: {
      type: "none",
    },
    responseFormat: "html",
    dataPath: "",
    fieldMappings: {
      id: "href", // Extract from post URL
      fileUrl: "coverUrl", // Will be constructed from thumbnail
      previewUrl: "src", // Thumbnail image
      sampleUrl: "coverUrl",
      tags: "title", // Use title as tags for now
      tagsDelimiter: " ",
      rating: "rating",
      score: "rating", // Use rating as score
      source: "href",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 18, // Based on what I saw on the homepage
    icon: "📖",
    faviconUrl: "https://www.google.com/s2/favicons?domain=xlecx.one&sz=64",
    description: "XlecX - Porn comics and multimedia content, with albums.",
    homepage: "https://xlecx.one",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    defaultNestedMode: true, // Enable nested navigation for albums
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "civitai",
    name: "Civitai",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    apiType: "civitai",
    endpoints: {
      search: "/api/booru/civitai/search",
      tags: "/api/booru/civitai/fuzzy",
    },
    auth: {
      type: "optional_api_key", // Platform key covers everyone by default; a user's own key is only needed as a rate-limit fallback
      apiKey: "",
    },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id",
      fileUrl: "fileUrl",
      previewUrl: "previewUrl",
      sampleUrl: "sampleUrl",
      tags: "tags",
      tagsDelimiter: " ",
      rating: "rating",
      score: "score",
      source: "source",
      width: "width",
      height: "height",
    },
    pagination: {
      type: "cursor",
      param: "cursor",
      startAt: 0,
    },
    limitParam: "limit",
    maxLimit: 200,
    icon: "🤖",
    faviconUrl: "https://www.google.com/s2/favicons?domain=civitai.com&sz=64",
    description: "Civitai - AI-generated image/video platform with model, LoRA, and artist metadata, fuzzy tag/artist search. Works out of the box; add your own account key in Source Settings only if you hit shared rate limits.",
    homepage: "https://civitai.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false, // Platform key covers this by default - don't nag users to add their own
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "goontai",
    name: "Goontai",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "goontai",
    kind: "video",
    endpoints: {
      search: "https://hentaiocean.com/rss.xml",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "slug",
      fileUrl: "thumbnail",
      previewUrl: "thumbnail",
      sampleUrl: "thumbnail",
      tags: "genres",
      tagsDelimiter: ",",
      rating: "rating",
      score: "score",
      source: "postUrl",
      width: "width",
      height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 1 },
    limitParam: "limit",
    maxLimit: 25,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "🐉",
    faviconUrl: "https://www.google.com/s2/favicons?domain=goontai.net&sz=64",
    description: "Goontai - Hentai anime videos with embed playback.",
    homepage: "https://hentaiocean.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "javapi",
    name: "JAVAPI",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "javapi",
    kind: "video",
    endpoints: {
      search: "https://server.apijav.com/wp-json/myvideo/v1/posts",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id", fileUrl: "thumbnail", previewUrl: "thumbnail",
      sampleUrl: "thumbnail", tags: "tags", tagsDelimiter: ",",
      rating: "rating", score: "views", source: "embed_url",
      width: "width", height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 1 },
    limitParam: "per_page",
    maxLimit: 100,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "",
    faviconUrl: "https://www.google.com/s2/favicons?domain=apijav.com&sz=64",
    description: "JAVAPI - Japanese adult videos. Search by actor, studio, category.",
    homepage: "https://apijav.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "javapi_hentai",
    name: "JAVAPI Hentai",
    type: "booru",
    enabled: true,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "javapi_hentai",
    kind: "video",
    endpoints: {
      search: "https://hentai.apijav.com/wp-json/myvideo/v1/posts",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id", fileUrl: "thumbnail", previewUrl: "thumbnail",
      sampleUrl: "thumbnail", tags: "tags", tagsDelimiter: ",",
      rating: "rating", score: "views", source: "embed_url",
      width: "width", height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 1 },
    limitParam: "per_page",
    maxLimit: 100,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "",
    faviconUrl: "https://www.google.com/s2/favicons?domain=hentai.apijav.com&sz=64",
    description: "JAVAPI Hentai - Hentai videos, focused on hentai content.",
    homepage: "https://hentai.apijav.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // ─── SIB: AudioChan ───────────────────────────────────────────────────────
  {
    id: "audiochan",
    name: "AudioChan",
    type: "api" as any,
    enabled: false, // User opts in
    fetchMode: "server",
    apiType: "generic" as any,
    endpoints: {
      search: "/api/audiochan/search",
    },
    auth: {
      type: "none",
    },
    responseFormat: "json",
    dataPath: "posts",
    fieldMappings: {
      id: "id",
      fileUrl: "audioUrl",
      previewUrl: "artistAvatar",
      sampleUrl: "artistAvatar",
      tags: "tags",
      tagsDelimiter: ",",
      rating: "rating",
      score: "plays",
      source: "postUrl",
      width: "0",
      height: "0",
    },
    pagination: {
      type: "page",
      param: "page",
      startAt: 1,
    },
    limitParam: "limit",
    maxLimit: 20,
    icon: "🎵",
    faviconUrl: "https://www.google.com/s2/favicons?domain=audiochan.com&sz=64",
    description: "AudioChan - NSFW audio content (erotic audio, ASMR, voice acting). Powers the SIB (Sound in Background) feature.",
    homepage: "https://audiochan.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "redgifs",
    name: "Redgifs",
    type: "booru",
    // New sources ship switched off; a visitor turns them on in Manage Sources.
    enabled: false,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "redgifs",
    endpoints: {
      search: "https://api.redgifs.com/v2/gifs/search",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id", fileUrl: "fileUrl", previewUrl: "previewUrl", sampleUrl: "sampleUrl",
      tags: "tags", tagsDelimiter: " ", rating: "rating", score: "score",
      source: "postUrl", width: "width", height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 0 },
    limitParam: "limit",
    maxLimit: 80,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "🎞️",
    faviconUrl: "https://www.google.com/s2/favicons?domain=www.redgifs.com&sz=64",
    description: "Redgifs - short videos and gifs, searched by tag.",
    homepage: "https://www.redgifs.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "scrolller",
    name: "Scrolller",
    type: "booru",
    // New sources ship switched off; a visitor turns them on in Manage Sources.
    enabled: false,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "scrolller",
    endpoints: {
      search: "https://api.scrolller.com/admin",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id", fileUrl: "fileUrl", previewUrl: "previewUrl", sampleUrl: "sampleUrl",
      tags: "tags", tagsDelimiter: " ", rating: "rating", score: "score",
      source: "postUrl", width: "width", height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 0 },
    limitParam: "limit",
    maxLimit: 50,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "📜",
    faviconUrl: "https://www.google.com/s2/favicons?domain=scrolller.com&sz=64",
    description: "Scrolller - pictures and videos from communities; typing a topic opens its best-matching community.",
    homepage: "https://scrolller.com",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "kemono",
    name: "Kemono",
    type: "booru",
    // New sources ship switched off; a visitor turns them on in Manage Sources.
    enabled: false,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "kemono",
    endpoints: {
      search: "https://kemono.cr/api/v1/posts",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id", fileUrl: "fileUrl", previewUrl: "previewUrl", sampleUrl: "sampleUrl",
      tags: "tags", tagsDelimiter: " ", rating: "rating", score: "score",
      source: "postUrl", width: "width", height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 0 },
    limitParam: "limit",
    maxLimit: 50,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "📦",
    faviconUrl: "https://www.google.com/s2/favicons?domain=kemono.cr&sz=64",
    description: "Kemono - creator posts (Patreon, Fanbox, Fantia and more), searched by text.",
    homepage: "https://kemono.cr",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: "pawchive",
    name: "Pawchive",
    type: "booru",
    enabled: false,
    // Out of service since 2026-10-25: pawchive.pw itself is down (Cloudflare Error 1033, its tunnel is offline, even the homepage), so every
    // search is empty. Listed greyed out at the bottom of Manage Sources. Remove these two lines to bring it back when the site is up
    // (the search code is a mirror of Kemono's and is untouched).
    disabled: true,
    showWhenDisabled: true,
    fetchMode: "server",
    useBooruPackage: false,
    apiType: "pawchive",
    endpoints: {
      search: "https://pawchive.pw/api/v1/posts",
      tags: "",
    },
    auth: { type: "none" },
    responseFormat: "json",
    dataPath: "",
    fieldMappings: {
      id: "id", fileUrl: "fileUrl", previewUrl: "previewUrl", sampleUrl: "sampleUrl",
      tags: "tags", tagsDelimiter: " ", rating: "rating", score: "score",
      source: "postUrl", width: "width", height: "height",
    },
    pagination: { type: "page", param: "page", startAt: 0 },
    limitParam: "limit",
    maxLimit: 50,
    ratingMappings: { safe: [], questionable: [], explicit: ["explicit"] },
    icon: "📦",
    faviconUrl: "https://www.google.com/s2/favicons?domain=pawchive.pw&sz=64",
    description: "Pawchive - a Kemono mirror: creator posts (Patreon, Fanbox, Fantia and more), searched by text.",
    homepage: "https://pawchive.pw",
    isDefault: true,
    isCustom: false,
    requiresAuth: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

// Sources that ship ENABLED for new visitors: Safebooru, Danbooru and Gelbooru only (Sakugabooru was dropped 2026-10-11: out of service) (safe-by-default,
// see "Project Fuck Stripe and Get Approved.md" -- may change based on tester feedback). Every other
// source ships switched OFF (not removed: they stay in the list as disabled entries and can be
// switched on in Manage Sources). Returning users keep whatever they already saved (see `merge`
// below), and nothing in BASE_DEFAULT_SOURCES was edited. Set to null to ship every source's own
// `enabled` flag instead.
const REVIEW_ONLY_ENABLED_SOURCES: readonly string[] | null = [
  "safebooru",
  "danbooru",
  "gelbooru",
]

const DEFAULT_SOURCES: SourceConfig[] = REVIEW_ONLY_ENABLED_SOURCES
  ? BASE_DEFAULT_SOURCES.map((source) => ({ ...source, enabled: REVIEW_ONLY_ENABLED_SOURCES.includes(source.id) }))
  : BASE_DEFAULT_SOURCES

/** Ids of the sources a brand-new visitor starts with enabled (used to pre-warm their first search). */
export const DEFAULT_ENABLED_SOURCE_IDS: string[] = DEFAULT_SOURCES.filter((s) => s.enabled).map((s) => s.id)

const USE_PRODUCTION_KEYS = process.env.GELBOORU_USE_PRODUCTION_KEYS === "true"
const USE_PRODUCTION_R34_KEYS = process.env.RULE34_USE_PRODUCTION_KEYS === "true"

const GELBOORU_KEYS = USE_PRODUCTION_KEYS
  ? [
      // Production mode: Use only the 5 GOONRU keys (no test key)
      {
        apiKey: process.env.GOONRU_GEL_KEY_1 || "",
        userId: process.env.GOONRU_GEL_ID_1 || "",
      },
      {
        apiKey: process.env.GOONRU_GEL_KEY_2 || "",
        userId: process.env.GOONRU_GEL_ID_2 || "",
      },
      {
        apiKey: process.env.GOONRU_GEL_KEY_3 || "",
        userId: process.env.GOONRU_GEL_ID_3 || "",
      },
      {
        apiKey: process.env.GOONRU_GEL_KEY_4 || "",
        userId: process.env.GOONRU_GEL_ID_4 || "",
      },
      {
        apiKey: process.env.GOONRU_GEL_KEY_5 || "",
        userId: process.env.GOONRU_GEL_ID_5 || "",
      },
    ].filter((key) => key.apiKey && key.userId)
  : [
      // Test mode: Use only your personal test key
      {
        apiKey: process.env.GELBOORU_API_KEY || "",
        userId: process.env.GELBOORU_USER_ID || "",
      },
    ].filter((key) => key.apiKey && key.userId)

// RULE34_CREDENTIALS is a comma-separated list of `apiKey:userId` pairs (each Rule34 key is limited to 60 requests a minute,
// so several are rotated). Server-side only; never exposed to the browser.
const RULE34_CREDENTIAL_KEYS = (process.env.RULE34_CREDENTIALS || "")
  .split(",")
  .map((pair) => pair.trim().split(":"))
  .map(([apiKey, userId]) => ({ apiKey: apiKey || "", userId: userId || "" }))
  .filter((key) => key.apiKey && key.userId)

const RULE34_KEYS = RULE34_CREDENTIAL_KEYS.length > 0 ? RULE34_CREDENTIAL_KEYS : USE_PRODUCTION_R34_KEYS
  ? [
      {
        apiKey: process.env.GOONRU_R34_KEY_1 || "",
        userId: process.env.GOONRU_R34_ID_1 || "",
      },
    ].filter((key) => key.apiKey && key.userId)
  : [
      // Test mode: Use your personal Rule34 key
      {
        apiKey: process.env.RULE34_API_KEY || "",
        userId: process.env.RULE34_USER_ID || "",
      },
    ].filter((key) => key.apiKey && key.userId)

let currentKeyIndex = 0
let currentR34KeyIndex = 0

export const getNextGelbooruKey = () => {
  if (GELBOORU_KEYS.length === 0) {
    console.log("[v0] No Gelbooru API keys configured - using anonymous access")
    return { apiKey: "", userId: "" }
  }
  const key = GELBOORU_KEYS[currentKeyIndex]
  currentKeyIndex = (currentKeyIndex + 1) % GELBOORU_KEYS.length
  return key
}

export const getNextRule34Key = () => {
  if (RULE34_KEYS.length === 0) {
    console.warn("[v0] No valid Rule34 API keys configured")
    return { apiKey: "", userId: "" }
  }
  const key = RULE34_KEYS[currentR34KeyIndex]
  currentR34KeyIndex = (currentR34KeyIndex + 1) % RULE34_KEYS.length
  return key
}

export const getAllGelbooruKeys = () => GELBOORU_KEYS

export const getAllRule34Keys = () => RULE34_KEYS

const useSourceStore = create<SourceStore>()(
  persist(
    (set, get) => ({
      sources: DEFAULT_SOURCES,

      addSource: (source: Omit<SourceConfig, "id" | "createdAt" | "updatedAt">) =>
        set((state: SourceStore) => ({
          sources: [
            ...state.sources,
            {
              ...source,
              id: Math.random().toString(36).substr(2, 9),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isDefault: false, // Mark new sources as not default
            },
          ],
        })),

      updateSource: (id: string, updates: Partial<SourceConfig>) =>
        set((state: SourceStore) => ({
          sources: state.sources.map((source: SourceConfig) => {
            if (source.id === id) {
              const preservedFlags = source.isDefault ? { isDefault: true, isCustom: false } : {}
              return {
                ...source,
                ...updates,
                ...preservedFlags,
                updatedAt: new Date().toISOString(),
              }
            }
            return source
          }),
        })),

      deleteSource: (id: string) =>
        set((state: SourceStore) => ({
          sources: state.sources.filter((source: SourceConfig) => source.id !== id),
        })),

      toggleSource: (id: string) => {
        console.log(`[SourceStore] Toggling source: ${id}`)
        set((state: SourceStore) => {
          const updatedSources = state.sources.map((source: SourceConfig) => {
            if (source.id === id) {
              const newEnabled = !source.enabled
              console.log(`[SourceStore] ${id}: ${source.enabled} → ${newEnabled}`)
              return { ...source, enabled: newEnabled, updatedAt: new Date().toISOString() }
            }
            return source
          })
          
          console.log(`[SourceStore] Updated sources:`, updatedSources.map(s => `${s.id}(${s.enabled ? 'enabled' : 'disabled'})`))
          
          // Force persistence by creating a completely new state object
          const newState = {
            sources: updatedSources
          }
          
          // Also force localStorage update
          setTimeout(() => {
            try {
              const storeData = {
                state: newState,
                version: 26
              }
              localStorage.setItem('source-storage', JSON.stringify(storeData))
              console.log(`[SourceStore] Force saved to localStorage`)
            } catch (error) {
              console.error(`[SourceStore] Failed to force save:`, error)
            }
          }, 100)
          
          return newState
        })
      },

      duplicateSource: (id: string) =>
        set((state: SourceStore) => {
          const sourceToDuplicate = state.sources.find((source: SourceConfig) => source.id === id)
          if (!sourceToDuplicate) return state
          return {
            sources: [
              ...state.sources,
              {
                ...sourceToDuplicate,
                id: Math.random().toString(36).substr(2, 9),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isDefault: false, // Mark duplicated sources as not default
              },
            ],
          }
        }),

      exportSources: () => JSON.stringify(get().sources, null, 2),

      importSources: (json: string) => {
        try {
          const importedSources = JSON.parse(json) as SourceConfig[]
          set({ sources: importedSources })
          return true
        } catch (e) {
          console.error("Failed to import sources:", e)
          return false
        }
      },

      getSource: (id: string) => get().sources.find((source: SourceConfig) => source.id === id),

      getAllSources: () => get().sources, // Added getAllSources method

      getEnabledSources: () => {
        const sources = get().sources
        const enabled = sources.filter((source: SourceConfig) => source.enabled && !source.disabled)
        console.log(`[SourceStore] getEnabledSources:`, enabled.map((s: { id: string }) => s.id))
        return enabled
      },

      resetToDefaults: () =>
        set({ sources: DEFAULT_SOURCES.filter((s: SourceConfig) => s.id !== "konachan" && s.id !== "furrybooru") }),

      testConnection: async (id: string) => {
        const source = get().getSource(id)
        if (!source) return { success: false, error: "Source not found" }

        if (source.auth.type === "oauth" && !source.auth.oauthToken) {
          return {
            success: false,
            error: "OAuth token is not configured. Open Edit and paste your bearer token.",
          }
        }

        try {
          if (source.fetchMode === "client") {
            const testUrl = new URL(source.endpoints.search)
            testUrl.searchParams.set(source.pagination.param, String(source.pagination.startAt))
            testUrl.searchParams.set(source.limitParam, "1")

            if (source.auth.apiKey) {
              testUrl.searchParams.set(source.auth.apiKeyParam || "api_key", source.auth.apiKey)
            }
            if (source.auth.userId) {
              testUrl.searchParams.set(source.auth.userIdParam || "user_id", source.auth.userId)
            }

            const response = await fetch(testUrl.toString(), {
              headers: {
                "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
              },
            })

            if (!response.ok) {
              return {
                success: false,
                error: `HTTP ${response.status}: ${response.statusText}`,
              }
            }

            if (source.responseFormat === "json") {
              await response.json()
              return { success: true }
            } else if (source.responseFormat === "xml") {
              await response.text()
              return { success: true }
            } else {
              await response.text()
              return { success: true }
            }
          }

          const response =
            source.isCustom
              ? await fetch(`/api/booru/test`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ sourceConfig: source }),
                })
              : await fetch(`/api/booru/test?site=${encodeURIComponent(id)}`)
          const data = await response.json()

          if (!response.ok) {
            return {
              success: false,
              error: data.error || `HTTP ${response.status}`,
            }
          }

          return {
            success: !!data.success,
            error: data.success ? undefined : data.error || "Unknown error",
          }
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : "Unknown error",
          }
        }
      },

      reorderSources: (startIndex: number, endIndex: number) => {
        const sources = [...get().sources]
        const [removed] = sources.splice(startIndex, 1)
        sources.splice(endIndex, 0, removed)
        set({ sources })
      },
    }),
    {
      name: "source-storage",
      version: 36, // Disable auth prompt for erome (no public API)
      migrate: (persistedState: any, version: number) => {
        sourceLog("[source-store] Migrating source store from version", version, "to 36")
        
        // Clean up old/deprecated sources
        if (persistedState && persistedState.sources) {
          persistedState.sources = persistedState.sources.filter(
            (s: SourceConfig) => s.id !== "sankaku_black" && s.id !== "furrybooru" && s.id !== "konachan"
          )
          
          // Update existing sources with latest configs
          persistedState.sources = persistedState.sources.map((s: SourceConfig) => {
            if (s.id === "idolsankaku") {
              return { 
                ...s, 
                name: "Idol Complex",
                homepage: "https://idolcomplex.com",
                faviconUrl: "https://www.google.com/s2/favicons?domain=idolcomplex.com&sz=64",
                dataPath: "",
                endpoints: {
                  ...s.endpoints,
                  tags: "https://iapi.sankakucomplex.com/tags"
                }
              }
            }
            if (s.id === "sankaku") {
              return {
                ...s,
                dataPath: "",
                endpoints: {
                  ...s.endpoints,
                  tags: "https://capi-v2.sankakucomplex.com/tags"
                }
              }
            }
            if (s.id === "rule34us") {
              return {
                ...s,
                auth: { type: "none" }
              }
            }
            if (s.id === "rule34lol") {
              return {
                ...s,
                requiresAuth: false,
              }
            }
            if (s.id === "rule34hentai") {
              return {
                ...s,
                disabled: true,
                enabled: false,
                icon: "⚠️",
                description: "⚠️ DISABLED - Images not loading properly.",
              }
            }
            if (s.id === "footfetishbooru") {
              return {
                ...s,
                disabled: true,
                enabled: false,
                icon: "⚠️",
                description: "⚠️ OUT OF SERVICE",
              }
            }
            // Fix Goontai icon - remove tagLimit:0 only
            if (s.id === "goontai") {
              const { tagLimit, ...rest } = s as any
              return {
                ...rest,
                icon: "🐉",
                faviconUrl: "https://www.google.com/s2/favicons?domain=goontai.net&sz=64",
              }
            }
            // Enable safebooru by default for existing users
            if (s.id === "safebooru") {
              return { ...s, enabled: true }
            }
            // Remove tagLimit:0 from JAVAPI sources
            if (s.id === "javapi" || s.id === "javapi_hentai") {
              const { tagLimit, ...rest } = s as any
              return rest
            }
            // Add version/category boards to existing erome configs that predate it,
            // and default unselected/untouched ("all") configs to "straight"
            if (s.id === "erome") {
              const available = [
                { id: "all", name: "All" },
                { id: "straight", name: "Straight" },
                { id: "gay", name: "Gay" },
                { id: "hentai", name: "Hentai" },
                { id: "trans", name: "Trans" },
              ]
              const currentlyAll = !s.boards || JSON.stringify(s.boards.selected) === JSON.stringify(["all"])
              return {
                ...s,
                requiresAuth: false,
                boards: {
                  available,
                  selected: currentlyAll ? ["straight"] : s.boards!.selected,
                  allowMultiple: false,
                },
              }
            }
            return s
          })
        }
        
        return persistedState || { sources: DEFAULT_SOURCES }
      },
      merge: (persistedState: any, currentState: any) => {
        sourceLog("[source-store] Merging persisted state with current state")
        if (persistedState && persistedState.sources && persistedState.sources.length > 0) {
          const byId = new Map<string, SourceConfig>(
            persistedState.sources.map((source: SourceConfig) => [source.id, source])
          )
          
          sourceLog("[source-store] Persisted sources:", Array.from(byId.keys()))
          sourceLog("[source-store] Default sources:", DEFAULT_SOURCES.map(s => s.id))
          
          // Add any missing default sources
          for (const source of DEFAULT_SOURCES) {
            if (!byId.has(source.id)) {
              sourceLog("[source-store] Adding missing default source:", source.id)
              byId.set(source.id, source)
            } else {
              const existingSource = byId.get(source.id)!

              // Default sources are admin-curated and no longer user-editable
              // (Edit is hidden in the UI for isDefault sources) — so their
              // config should always reflect the current code, not whatever
              // was persisted when the user first loaded the app. Refresh
              // everything from the fresh default EXCEPT the handful of
              // fields that are genuinely per-user state (enabled toggle,
              // board/category selection, and the out-of-service flag,
              // which is set centrally too but shouldn't fight a user who
              // already re-enabled a source before it was marked disabled).
              if (source.isDefault) {
                // 4chan is one board at a time now: a saved list of several boards (the old default) becomes "none".
                const savedBoards = source.id === "4chan" && (existingSource.boards?.selected.length ?? 0) > 1 ? source.boards : existingSource.boards
                byId.set(source.id, {
                  ...source,
                  enabled: existingSource.enabled,
                  boards: savedBoards ?? source.boards,
                })
                continue
              }

              // Force update specific sources to ensure latest config
              if (source.id === "doujins") {
                sourceLog("[source-store] Force updating Doujins source")
                byId.set(source.id, {
                  ...source,
                  enabled: existingSource.enabled !== undefined ? existingSource.enabled : true,
                })
              } else if (source.id === "hitomi") {
                sourceLog("[source-store] Force updating Hitomi source")
                byId.set(source.id, {
                  ...source,
                  enabled: existingSource.enabled !== undefined ? existingSource.enabled : true,
                })
              } else if (!existingSource.faviconUrl && source.faviconUrl) {
                // Update favicon if missing
                byId.set(source.id, { ...existingSource, faviconUrl: source.faviconUrl })
              } else if (
                source.isDefault &&
                !existingSource.isDefault &&
                ["xvideos", "xbooru", "audiochan", "rule34hentai", "footfetishbooru"].includes(source.id)
              ) {
                // These ship as part of DEFAULT_SOURCES but were persisted with a stale
                // isDefault:false, which wrongly showed a Delete button on them - correct
                // it in place without touching anything else the user has set (enabled, etc).
                sourceLog("[source-store] Correcting stale isDefault:false on shipped source:", source.id)
                byId.set(source.id, { ...existingSource, isDefault: true })
              }
            }
          }
          
          const mergedSources = Array.from(byId.values())
          sourceLog("[source-store] Merged sources:", mergedSources.map(s => `${s.id}(${s.enabled ? 'enabled' : 'disabled'})`))
          
          return { ...currentState, ...persistedState, sources: mergedSources }
        }
        
        sourceLog("[source-store] No persisted state, using DEFAULT_SOURCES")
        return { ...currentState, sources: DEFAULT_SOURCES }
      },
    },
  ),
)

export { useSourceStore }
